'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import CTAButton from './CTAButton';
import { corporateTheme } from '@/lib/theme/corporateTheme';
import { useAuth } from '@/contexts/AuthContext';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState<number>(2026);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    services: [
      { label: 'Image Enhancement', href: '/services/image-enhancement' },
      { label: 'Virtual Staging', href: '/services/virtual-staging' },
      { label: 'Day to Dusk', href: '/services/day-to-dusk' },
      { label: 'Item Removal', href: '/services/item-removal' },
      { label: 'Floor Plans', href: '/services/floor-plans' },
      { label: 'Virtual Renovation', href: '/services/virtual-renovation' }
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'Careers', href: '/careers' },
      { label: 'Blog', href: '/blog' }
    ],
    support: [
      { label: 'Help Center', href: '/help' },
      { label: 'Live Chat', href: '#' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy' }
    ]
  };

  const socialLinks = [
    { name: 'Facebook', icon: 'ShareIcon', href: '#' },
    { name: 'Twitter', icon: 'ChatBubbleLeftRightIcon', href: '#' },
    { name: 'Instagram', icon: 'CameraIcon', href: '#' },
    { name: 'LinkedIn', icon: 'BriefcaseIcon', href: '#' }
  ];

  return (
    <footer 
      className="text-white relative overflow-hidden"
      style={{ 
        backgroundColor: '#0B0F14',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100\' height=\'100\' filter=\'url(%23noise)\' opacity=\'0.03\'/%3E%3C/svg%3E")',
      }}
    >
      {/* Footer CTA Section - Enhanced with gradient and modern design */}
      <div 
        className="border-b relative"
        style={{
          backgroundColor: 'transparent',
          borderColor: 'rgba(245, 245, 242, 0.1)',
        }}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-50"></div>
        
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20 text-center relative z-10">
          <h2
            className="mb-4"
            style={{
              fontSize: '42px',
              fontWeight: 800,
              color: '#F5F5F2',
              fontFamily: corporateTheme.typography.fontFamily.primary,
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Ready to Transform Your Images?
          </h2>
          <p
            className="mb-6 max-w-2xl mx-auto"
            style={{
              fontSize: '18px',
              fontWeight: corporateTheme.typography.weight.regular,
              color: '#A1A1AA',
              fontFamily: corporateTheme.typography.fontFamily.primary,
              lineHeight: '1.6',
            }}
          >
            Join 500+ real estate professionals. Pay only after you're satisfied with the results.
          </p>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center items-center gap-6 mb-8">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-300">No Credit Card</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-300">Pay After Editing</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-300">24hr Turnaround</span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link href={isAuthenticated ? '/client-dashboard' : '/auth/signup'} aria-label="Start Free - Pay After Editing">
              <button 
                className="px-10 py-4 font-bold text-lg transition-all duration-300 rounded-lg footer-cta-button shadow-xl"
                style={{
                  backgroundColor: '#F5F5F2',
                  color: '#0B0F14',
                  height: '56px',
                  fontWeight: 700,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(245, 245, 242, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#F5F5F2';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(245, 245, 242, 0.2)';
                }}
              >
                Start Free — Pay After Editing
              </button>
            </Link>
            <button 
              onClick={() => {
                const pricingSection = document.getElementById('pricing-section');
                pricingSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-10 py-4 font-bold text-lg transition-all duration-300 rounded-lg"
              style={{
                backgroundColor: 'transparent',
                color: '#F5F5F2',
                border: '2px solid rgba(245, 245, 242, 0.3)',
                height: '56px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(245, 245, 242, 0.6)';
                e.currentTarget.style.backgroundColor = 'rgba(245, 245, 242, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(245, 245, 242, 0.3)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              View Pricing
            </button>
          </div>
        </div>
      </div>

      {/* Footer Links Section - Enhanced with modern styling */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-3 mb-6 group">
              <img 
                src="/shashio-logo.svg" 
                alt="Shashio Logo" 
                className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
              />
              <div className="flex items-center">
                <span 
                  className="text-lg font-normal transition-colors duration-300"
                  style={{ color: '#F5F5F2' }}
                >
                  shashio.com
                </span>
              </div>
            </Link>
            <p 
              className="mb-8 max-w-md leading-relaxed"
              style={{ 
                color: '#71717A',
                fontSize: '15px',
                lineHeight: '1.7',
              }}
            >
              Professional photo editing, virtual staging, and rendering services at unbeatable prices. 
              Trusted by 500+ real estate professionals worldwide.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8 max-w-md">
              <div>
                <div className="text-2xl font-bold text-white mb-1">500+</div>
                <div className="text-xs text-gray-500">Clients</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white mb-1">50K+</div>
                <div className="text-xs text-gray-500">Images</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white mb-1">4.9★</div>
                <div className="text-xs text-gray-500">Rating</div>
              </div>
            </div>
            
            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-lg flex items-center justify-center transition-all duration-300"
                  aria-label={social.name}
                  style={{ 
                    backgroundColor: 'rgba(245, 245, 242, 0.05)',
                    border: '1px solid rgba(245, 245, 242, 0.1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(245, 245, 242, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(245, 245, 242, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(245, 245, 242, 0.05)';
                    e.currentTarget.style.borderColor = 'rgba(245, 245, 242, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Icon name={social.icon as any} size={18} variant="outline" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 
              className="text-sm font-bold mb-6 tracking-wider"
              style={{ color: '#F5F5F2' }}
            >
              SERVICES
            </h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm transition-all duration-200 inline-block py-1 hover:translate-x-1"
                    style={{ color: '#71717A' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#F5F5F2'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#71717A'}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 
              className="text-sm font-bold mb-6 tracking-wider"
              style={{ color: '#F5F5F2' }}
            >
              COMPANY
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm transition-all duration-200 inline-block py-1 hover:translate-x-1"
                    style={{ color: '#71717A' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#F5F5F2'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#71717A'}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 
              className="text-sm font-bold mb-6 tracking-wider"
              style={{ color: '#F5F5F2' }}
            >
              SUPPORT
            </h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm transition-all duration-200 inline-block py-1 hover:translate-x-1"
                    style={{ color: '#71717A' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#F5F5F2'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#71717A'}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div 
          className="border-t pt-10"
          style={{ borderColor: 'rgba(245, 245, 242, 0.1)' }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            <div className="flex flex-col items-center md:items-start space-y-2">
              <p 
                className="text-sm"
                style={{ color: '#52525B' }}
              >
                {isHydrated ? `© ${currentYear}` : '© 2026'} Shashio.com. All rights reserved.
              </p>
              <p 
                className="text-xs"
                style={{ color: '#3F3F46' }}
              >
                Professional photo editing services for real estate
              </p>
            </div>
            <div className="flex items-center space-x-8">
              <Link 
                href="/terms" 
                className="text-sm transition-colors duration-200 py-2"
                style={{ color: '#52525B' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#F5F5F2'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#52525B'}
              >
                Terms
              </Link>
              <Link 
                href="/privacy" 
                className="text-sm transition-colors duration-200 py-2"
                style={{ color: '#52525B' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#F5F5F2'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#52525B'}
              >
                Privacy
              </Link>
              <a 
                href="#" 
                className="text-sm transition-colors duration-200 py-2"
                style={{ color: '#52525B' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#F5F5F2'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#52525B'}
              >
                Cookies
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Responsive Styles for Footer CTA */}
      <style jsx>{`
        @media (max-width: 767px) {
          .footer-cta-button {
            min-height: 44px !important;
          }
          h2 {
            font-size: 24px !important;
          }
          p {
            font-size: 16px !important;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;